import React, { useState } from 'react';
import { Camera, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Gallery } from '../components/Gallery';
import { appConfig, getUploadLimits, isFeatureEnabled } from '../config/app.config';
import { uploadImage } from '../lib/api';

interface FileWithPreview {
  file: File;
  preview: string;
  error?: string;
}

function Home() {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [hasClicked, setHasClicked] = useState(false);

  const uploadLimits = getUploadLimits();
  const uploadEnabled = isFeatureEnabled('enableUpload');

  const validateFile = (file: File, currentTotalSize: number): { isValid: boolean; error: string | null } => {
    if (!uploadLimits.allowedTypes.includes(file.type.toLowerCase())) {
      return {
        isValid: false,
        error: `Nieodpowiedni format pliku. Dozwolone formaty to: JPG, JPEG i PNG. Wybrany format: ${file.type || 'nieznany'}`
      };
    }

    if (file.size > uploadLimits.maxFileSize) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const maxSizeMB = (uploadLimits.maxFileSize / (1024 * 1024)).toFixed(0);
      return {
        isValid: false,
        error: `Plik jest za duży. Maksymalny rozmiar to ${maxSizeMB}MB. Rozmiar wybranego pliku: ${sizeMB}MB`
      };
    }

    if (currentTotalSize + file.size > uploadLimits.maxTotalSize) {
      const remainingMB = ((uploadLimits.maxTotalSize - currentTotalSize) / (1024 * 1024)).toFixed(2);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const maxTotalMB = (uploadLimits.maxTotalSize / (1024 * 1024)).toFixed(0);
      return {
        isValid: false,
        error: `Przekroczono łączny limit rozmiaru (${maxTotalMB}MB). Pozostało: ${remainingMB}MB. Rozmiar pliku: ${fileSizeMB}MB`
      };
    }

    return { isValid: true, error: null };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remainingSlots = uploadLimits.maxFiles - selectedFiles.length;
    
    if (files.length > remainingSlots) {
      toast.error(`Możesz dodać maksymalnie ${uploadLimits.maxFiles} zdjęć jednocześnie`);
      return;
    }

    const currentTotalSize = selectedFiles.reduce((total, file) => total + file.file.size, 0);
    const newFiles: FileWithPreview[] = [];
    let newTotalSize = currentTotalSize;

    for (const file of files) {
      const { isValid, error } = validateFile(file, newTotalSize);
      if (isValid) {
        newTotalSize += file.size;
        newFiles.push({
          file,
          preview: URL.createObjectURL(file)
        });
      } else {
        newFiles.push({
          file,
          preview: URL.createObjectURL(file),
          error
        });
      }
    }

    setSelectedFiles(prev => [...prev, ...newFiles].slice(0, uploadLimits.maxFiles));
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (selectedFiles.length === 0) return;

    const hasErrors = selectedFiles.some(f => f.error);
    if (hasErrors) {
      toast.error('Niektóre pliki zawierają błędy');
      return;
    }

    setIsUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const { file } of selectedFiles) {
      try {
        await uploadImage(file);
        successCount++;
      } catch (error) {
        console.error('Upload error:', error);
        errorCount++;
      }
    }

    selectedFiles.forEach(f => URL.revokeObjectURL(f.preview));

    if (successCount > 0) {
      const message = successCount === 1 
        ? 'Zdjęcie zostało dodane!'
        : `${successCount} zdjęć zostało dodanych!`;
      toast.success(message);
    }

    if (errorCount > 0) {
      const message = errorCount === 1
        ? 'Nie udało się dodać 1 zdjęcia'
        : `Nie udało się dodać ${errorCount} zdjęć`;
      toast.error(message);
    }

    setIsUploadModalOpen(false);
    setSelectedFiles([]);
    setIsUploading(false);
  }

  const handleAddPhotoClick = () => {
    if (!uploadEnabled) {
      toast.error('Funkcja dodawania zdjęć jest obecnie wyłączona');
      return;
    }
    setHasClicked(true);
    setIsUploadModalOpen(true);
  };

  const getCurrentTotalSize = () => {
    return selectedFiles.reduce((total, file) => total + file.file.size, 0);
  };

  const formatSize = (bytes: number) => {
    return `${(bytes / (1024 * 1024)).toFixed(2)}MB`;
  };

  const maxSizeMB = (uploadLimits.maxTotalSize / (1024 * 1024)).toFixed(0);

  return (
    <div className="max-w-7xl mx-auto">
      {uploadEnabled && (
        <div className="mb-6 text-center">
          <button
            onClick={handleAddPhotoClick}
            className={`ripple bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg inline-flex items-center space-x-2 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl ${
              !hasClicked ? 'pulse-animation' : ''
            }`}
          >
            <Camera className="w-5 h-5" />
            <span>Dodaj zdjęcie</span>
          </button>
        </div>
      )}

      <div className="mb-12">
        <h2 className="text-4xl font-normal mb-6 font-['Indie_Flower'] text-center">Najnowsze zdjęcia</h2>
        <Gallery />
      </div>

      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50">
          <div className="bg-[#f4f1ea] p-8 max-w-2xl w-full relative transform transition-all duration-300 modal-animation rounded-lg shadow-2xl">
            <div className="absolute -top-4 -right-4">
              <button
                onClick={() => {
                  selectedFiles.forEach(f => URL.revokeObjectURL(f.preview));
                  setSelectedFiles([]);
                  setIsUploadModalOpen(false);
                }}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors shadow-lg"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <h2 className="text-3xl font-['Indie_Flower'] mb-6 text-gray-800">Dodaj zdjęcia</h2>
            
            <form onSubmit={handleUpload} className="space-y-6">
              <div>
                <label className="block mb-4">
                  <span className="font-['Indie_Flower'] text-2xl text-gray-800 block mb-1">
                    Wybierz zdjęcia
                  </span>
                  <span className="text-sm text-gray-600 block">
                    Maksymalnie {uploadLimits.maxFiles} zdjęć, do {(uploadLimits.maxFileSize / (1024 * 1024)).toFixed(0)}MB każde, łącznie do {maxSizeMB}MB
                  </span>
                </label>

                <div className="bg-white p-6 rounded-lg shadow-inner mb-4">
                  <div className="text-sm text-gray-600 mb-3">
                    Wykorzystano: {formatSize(getCurrentTotalSize())} z {maxSizeMB}MB
                  </div>
                  
                  <input
                    type="file"
                    accept={uploadLimits.allowedTypes.join(',')}
                    onChange={handleFileChange}
                    className="w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100 file:cursor-pointer file:transition-colors"
                    multiple
                    disabled={selectedFiles.length >= uploadLimits.maxFiles}
                  />
                </div>

                {selectedFiles.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="relative bg-white p-2 rounded-lg shadow-md">
                        <img
                          src={file.preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full aspect-square object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        {file.error && (
                          <div className="absolute inset-0 bg-black/75 flex items-center justify-center rounded-lg p-2">
                            <p className="text-white text-xs text-center">{file.error}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t border-gray-300">
                <button
                  type="button"
                  onClick={() => {
                    selectedFiles.forEach(f => URL.revokeObjectURL(f.preview));
                    setSelectedFiles([]);
                    setIsUploadModalOpen(false);
                  }}
                  className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={isUploading || selectedFiles.length === 0 || selectedFiles.some(f => f.error)}
                  className="ripple bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl"
                >
                  {isUploading ? 'Dodawanie...' : 'Dodaj'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;